/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '@/components/layout/AdminLayout';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import {
  RefreshCw, AlertTriangle, Loader2, CheckCircle2, ArrowRight,
  GitBranch, Trash2, Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const TYPE_OPTIONS = [
  { value: 'unset', label: '— Choose —', tone: 'bg-rose-500/10 text-rose-300 border-rose-500/30' },
  { value: 'finish_first', label: 'Finish first' },
  { value: 'continue_same', label: 'Continue same' },
  { value: 'start_after_previous_finished', label: 'Start after…' },
  { value: 'parallel_allowed', label: 'Parallel allowed' },
  { value: 'stop_immediately', label: 'Stop immediately' },
];

const STATUS_TONE = {
  draft: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
  in_review: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  approved: 'bg-cyan-500/15 text-cyan-200 border-cyan-500/30',
  active: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  archived: 'bg-white/10 text-white/40 border-white/10',
};

const WARNING_LABEL = {
  inventory_mismatch_detected: 'Inventory mismatch detected (prior phase)',
  low_adherence_caution: 'Low adherence in prior phase (<70%)',
  open_feedback_issues: 'Open intolerance / doubt feedback signals',
};

export default function FormulationTransitionPlan() {
  const { planId } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canExplain = user?.role === 'admin' || user?.role === 'counselor';

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [moving, setMoving] = useState(false);

  const [items, setItems] = useState([]);
  const [adminSummary, setAdminSummary] = useState('');
  const [counselorExplanation, setCounselorExplanation] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const r = await axios.get(`${API}/formulation/transition-plan/${planId}`);
      setPlan(r.data.plan);
      setItems(r.data.plan.items || []);
      setAdminSummary(r.data.plan.admin_summary || '');
      setCounselorExplanation(r.data.plan.counselor_explanation || '');
    } catch (e) {
      setPageError(e?.response?.data?.detail || 'Could not load plan');
    } finally {
      setLoading(false);
    }
  }, [planId]);
  useEffect(() => { load(); }, [load]);

  const updateItem = (id, patch) => {
    setItems((cur) => cur.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  };

  const saveItems = async () => {
    if (!plan || plan.status !== 'draft' || !isAdmin) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API}/formulation/transition-plan/${plan.id}/items`,
        { items },
      );
      toast.success('Items saved');
      await load();
    } catch (e) {
      const data = e?.response?.data?.detail;
      if (typeof data === 'object' && data?.errors) {
        toast.error(`Validation: ${data.errors[0]?.error}`);
      } else {
        toast.error(typeof data === 'string' ? data : 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const saveExplanation = async () => {
    if (!plan) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API}/formulation/transition-plan/${plan.id}/explanation`,
        { admin_summary: adminSummary || null,
          counselor_explanation: counselorExplanation || null },
      );
      toast.success('Explanation saved');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const move = async (toStatus) => {
    if (!plan || !isAdmin) return;
    setMoving(true);
    try {
      const r = await axios.patch(
        `${API}/formulation/transition-plan/${plan.id}/status`,
        { to_status: toStatus },
      );
      setPlan(r.data.plan);
      toast.success(`Moved to ${toStatus}`);
    } catch (e) {
      const data = e?.response?.data?.detail;
      if (typeof data === 'object' && data?.unset_rows) {
        toast.error(`Cannot move: ${data.unset_rows.length} row(s) still unset`);
      } else {
        toast.error(typeof data === 'string' ? data : 'Status change failed');
      }
    } finally {
      setMoving(false);
    }
  };

  if (!user) return null;
  const isDraft = plan?.status === 'draft';
  const hasUnset = items.some((i) => i.transition_type === 'unset');
  const finishFirstItems = items.filter((i) => i.transition_type === 'finish_first');

  return (
    <AdminLayout>
      <div
        data-testid="formulation-transition-plan-page"
        className="px-6 py-6 max-w-6xl mx-auto"
      >
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-1">
            <GitBranch className="w-6 h-6 text-cyan-300" />
            <h1 className="text-2xl font-semibold text-white">Phase Transition Planner</h1>
            <Badge className="bg-cyan-500/15 text-cyan-200 border-cyan-500/30 text-[10px]">F5</Badge>
            {plan && (
              <span
                data-testid="transition-plan-status-pill"
                className={`px-2 py-0.5 rounded border text-[10px] ${STATUS_TONE[plan.status] || ''}`}
              >
                {plan.status}
              </span>
            )}
          </div>
          <p className="text-sm text-white/50">
            Convert an approved snapshot into one explicit, deterministic next-phase plan.
            Every transition decision is captured here.
          </p>
        </div>

        {loading && (
          <div className="rounded border border-white/10 bg-slate-900/40 p-8 text-center text-white/60">
            <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" /> Loading…
          </div>
        )}

        {pageError && !loading && (
          <div data-testid="transition-plan-page-error"
               className="rounded border border-amber-500/30 bg-amber-500/5 p-6 text-center">
            <AlertTriangle className="w-5 h-5 mx-auto text-amber-300 mb-2" />
            <div className="text-sm text-white/80">{pageError}</div>
          </div>
        )}

        {plan && (
          <>
            <PlanMeta plan={plan} />
            <WarningsPanel warnings={plan.advisory_warnings || []} />

            <ItemsTable
              items={items}
              isDraft={isDraft}
              isAdmin={isAdmin}
              finishFirstItems={finishFirstItems}
              onUpdate={updateItem}
            />
            {isAdmin && isDraft && (
              <div className="flex justify-end mb-4">
                <Button
                  data-testid="transition-plan-save-items-btn"
                  onClick={saveItems}
                  disabled={saving}
                  className="bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-500/30 h-9 text-xs"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                  Save items
                </Button>
              </div>
            )}

            {/* Explanations */}
            <div className="rounded-lg border border-white/10 bg-slate-900/40 p-3 mb-3">
              <div className="text-[10px] uppercase tracking-wide text-white/40 mb-2">
                Admin summary (internal)
              </div>
              <Textarea
                data-testid="transition-plan-admin-summary"
                value={adminSummary}
                onChange={(e) => setAdminSummary(e.target.value)}
                disabled={!isAdmin || plan.status === 'active' || plan.status === 'archived'}
                rows={2}
                maxLength={1000}
                placeholder="Internal rationale (not shown to subscriber)"
                className="bg-slate-950/40 border-white/10 text-sm mb-3"
              />
              <div className="text-[10px] uppercase tracking-wide text-white/40 mb-2">
                Counselor explanation (shown to subscriber)
              </div>
              <Textarea
                data-testid="transition-plan-counselor-explanation"
                value={counselorExplanation}
                onChange={(e) => setCounselorExplanation(e.target.value)}
                disabled={!canExplain || plan.status === 'active' || plan.status === 'archived'}
                rows={3}
                maxLength={500}
                placeholder="Plain-language note for the subscriber (≤500 chars)"
                className="bg-slate-950/40 border-white/10 text-sm"
              />
              {(canExplain && (plan.status !== 'active' && plan.status !== 'archived')) && (
                <div className="flex justify-end mt-2">
                  <Button
                    data-testid="transition-plan-save-explanation-btn"
                    onClick={saveExplanation}
                    disabled={saving}
                    variant="outline"
                    className="border-white/15 text-white/80 h-8 text-xs"
                  >
                    {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                    Save explanation
                  </Button>
                </div>
              )}
            </div>

            {/* Lifecycle controls */}
            {isAdmin && plan.status !== 'archived' && (
              <LifecycleControls
                status={plan.status}
                disabled={moving}
                hasUnset={hasUnset}
                onMove={move}
              />
            )}

            <ReviewHistory history={plan.review_history || []} />
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function PlanMeta({ plan }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 mb-3 text-xs">
      <div className="flex flex-wrap gap-3 text-white/60">
        <span><span className="text-white/40">subscriber:</span> <span className="font-mono">{plan.subscriber_id}</span></span>
        <span><span className="text-white/40">from phase:</span> <span className="font-mono">{plan.from_phase_id}</span></span>
        <span><span className="text-white/40">to phase:</span> <span className="font-mono">{plan.to_phase_id || '—'}</span></span>
        <span><span className="text-white/40">snapshot:</span> <span className="font-mono">{plan.snapshot_id}</span></span>
        {plan.approved_at && (
          <span><span className="text-white/40">approved:</span> {plan.approved_at.slice(0, 19).replace('T', ' ')}</span>
        )}
      </div>
    </div>
  );
}

function WarningsPanel({ warnings }) {
  if (!warnings || warnings.length === 0) return null;
  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 mb-3">
      <div className="text-[10px] uppercase tracking-wide text-amber-300 mb-2">
        Advisory — informational, never blocks
      </div>
      <ul className="space-y-1 text-xs">
        {warnings.map((w) => (
          <li
            key={w}
            data-testid={`transition-plan-warning-${w}`}
            className="text-amber-200 flex items-center gap-2"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-amber-300" />
            {WARNING_LABEL[w] || w}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ItemsTable({ items, isDraft, isAdmin, finishFirstItems, onUpdate }) {
  return (
    <div
      data-testid="transition-plan-items-table"
      className="rounded-lg border border-white/10 bg-slate-900/40 overflow-x-auto mb-3"
    >
      <table className="w-full text-xs">
        <thead className="bg-slate-950/60 border-b border-white/10 text-white/50">
          <tr>
            <th className="text-left px-3 py-2 font-medium">#</th>
            <th className="text-left px-3 py-2 font-medium">From</th>
            <th className="text-left px-3 py-2 font-medium">Type</th>
            <th className="text-left px-3 py-2 font-medium">To</th>
            <th className="text-left px-3 py-2 font-medium">Subscriber instruction</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <ItemRow
              key={it.id}
              item={it}
              isDraft={isDraft}
              isAdmin={isAdmin}
              finishFirstItems={finishFirstItems}
              onUpdate={onUpdate}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ItemRow({ item, isDraft, isAdmin, finishFirstItems, onUpdate }) {
  const editable = isDraft && isAdmin;
  return (
    <tr
      data-testid={`transition-plan-row-${item.id}`}
      className={`border-b border-white/5 ${item.transition_type === 'unset' ? 'bg-rose-500/5' : ''}`}
    >
      <td className="px-3 py-2 text-white/40">{item.priority_order}</td>
      <td className="px-3 py-2">
        {item.from_item ? (
          <div>
            <div className="text-white/80">{item.from_item.item_name}</div>
            <div className="text-[10px] text-cyan-300 font-mono">{item.from_item.raw_base_code}</div>
            <div className="text-[10px] text-white/40">remaining: {item.remaining_quantity}</div>
          </div>
        ) : (
          <span className="text-white/30">— new —</span>
        )}
      </td>
      <td className="px-3 py-2">
        <Select
          value={item.transition_type}
          onValueChange={(v) => onUpdate(item.id, {
            transition_type: v,
            // Clear starts_after_item_id if leaving start_after_*.
            starts_after_item_id: v === 'start_after_previous_finished'
              ? item.starts_after_item_id : null,
          })}
          disabled={!editable}
        >
          <SelectTrigger
            data-testid={`transition-plan-row-type-select-${item.id}`}
            className="bg-slate-950/40 border-white/10 text-xs h-8 w-44"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-white/10">
            {TYPE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {item.transition_type === 'start_after_previous_finished' && (
          <Select
            value={item.starts_after_item_id || ''}
            onValueChange={(v) => onUpdate(item.id, { starts_after_item_id: v })}
            disabled={!editable}
          >
            <SelectTrigger
              data-testid={`transition-plan-row-starts-after-${item.id}`}
              className="bg-slate-950/40 border-white/10 text-xs h-7 w-44 mt-1"
            >
              <SelectValue placeholder="…after which item?" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-white/10">
              {finishFirstItems.length === 0 && (
                <SelectItem value="-" disabled>No 'finish first' items</SelectItem>
              )}
              {finishFirstItems.map((ff) => (
                <SelectItem key={ff.id} value={ff.id}>
                  {ff.from_item?.item_name || '—'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </td>
      <td className="px-3 py-2">
        {item.to_item ? (
          <div>
            <div className="text-white/80">{item.to_item.item_name}</div>
            <div className="text-[10px] text-cyan-300 font-mono">{item.to_item.raw_base_code}</div>
          </div>
        ) : (
          <span className="text-white/30">—</span>
        )}
      </td>
      <td className="px-3 py-2 max-w-md">
        <Input
          data-testid={`transition-plan-row-instruction-input-${item.id}`}
          value={item.subscriber_instruction || ''}
          maxLength={200}
          onChange={(e) => onUpdate(item.id, { subscriber_instruction: e.target.value })}
          disabled={!editable}
          placeholder="e.g., Take both bottles you have left first."
          className="bg-slate-950/40 border-white/10 text-xs h-8"
        />
        <div className="text-[9px] text-white/30 mt-1">
          {(item.subscriber_instruction || '').length}/200
        </div>
      </td>
    </tr>
  );
}

function LifecycleControls({ status, disabled, hasUnset, onMove }) {
  return (
    <div className="rounded-lg border border-white/10 bg-slate-900/40 p-3 mb-3 flex flex-wrap items-center gap-2">
      <span className="text-xs text-white/50 mr-2">Lifecycle:</span>
      {status === 'draft' && (
        <Button
          data-testid="transition-plan-move-review-btn"
          size="sm"
          variant="outline"
          disabled={disabled || hasUnset}
          title={hasUnset ? 'Resolve all unset rows first' : ''}
          onClick={() => onMove('in_review')}
          className="border-white/15 text-white/80 h-7 text-[11px]"
        >
          Move to In review
        </Button>
      )}
      {status === 'in_review' && (
        <>
          <Button
            data-testid="transition-plan-back-to-draft-btn"
            size="sm"
            variant="outline"
            disabled={disabled}
            onClick={() => onMove('draft')}
            className="border-white/15 text-white/60 h-7 text-[11px]"
          >
            Back to Draft
          </Button>
          <Button
            data-testid="transition-plan-approve-btn"
            size="sm"
            disabled={disabled}
            onClick={() => onMove('approved')}
            className="bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-500/30 h-7 text-[11px]"
          >
            <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
          </Button>
        </>
      )}
      {status === 'approved' && (
        <Button
          data-testid="transition-plan-activate-btn"
          size="sm"
          disabled={disabled}
          onClick={() => onMove('active')}
          className="bg-emerald-500/20 text-emerald-200 border border-emerald-500/30 hover:bg-emerald-500/30 h-7 text-[11px]"
        >
          Activate (visible to subscriber)
        </Button>
      )}
      <Button
        data-testid="transition-plan-archive-btn"
        size="sm"
        variant="outline"
        disabled={disabled}
        onClick={() => onMove('archived')}
        className="border-white/15 text-white/40 h-7 text-[11px] ml-auto"
      >
        Archive
      </Button>
    </div>
  );
}

function ReviewHistory({ history }) {
  if (!history.length) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-slate-900/40 p-3 text-xs">
      <div className="text-[10px] uppercase tracking-wide text-white/40 mb-2">History</div>
      <ul className="space-y-1">
        {history.slice(-15).reverse().map((h, i) => (
          <li key={i} className="flex flex-wrap gap-2 items-center text-white/60">
            <span className="text-white/40">{new Date(h.at).toLocaleString()}</span>
            <span className="text-cyan-200 font-mono">{h.kind}</span>
            {h.from_status && h.to_status && (
              <span>
                <span className="text-white/40">{h.from_status}</span>
                <ArrowRight className="w-3 h-3 inline mx-1 text-white/30" />
                <span className="text-white/80">{h.to_status}</span>
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
